import { memo } from 'react'

import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom'
import { legacyRedirects } from 'shared/constants/legacyRedirects'
import { APP_ROUTES } from 'shared/constants/routes'
import { useExternalLogin } from 'shared/hooks/useExternalLogin'
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn'
import { useRedirectClassicUser } from 'shared/hooks/useRedirectClassicUser'
import { useUserContext } from 'shared/hooks/useUserContext'

import { LegacyClusterOfficeRedirect } from './LegacyClusterOfficeRedirect'
import {
  GuestRoute,
  ProtectedRoute,
  usePublicRoutes
} from './shared/components'

interface RouteTreeProps {
  isLoggedIn: boolean
}

const RouteTree = memo(({ isLoggedIn }: RouteTreeProps) => {
  const { user } = useUserContext()
  const publicRoutes = usePublicRoutes(isLoggedIn)

  useExternalLogin()
  useRedirectClassicUser(user)

  const { authCheck, ...appRoutes } = APP_ROUTES

  return (
    <RouterRoutes>
      <Route path={authCheck.path} element={<authCheck.component />} />

      <Route
        path='/cluster-space'
        element={<Navigate to='/cluster-files' replace />}
      />

      <Route
        path='/cluster-office/*'
        element={<LegacyClusterOfficeRedirect />}
      />

      {legacyRedirects.map(({ oldPath, component: Component }) => (
        <Route key={oldPath} path={oldPath} element={<Component />} />
      ))}

      {publicRoutes}

      {Object.values(appRoutes).map(route => (
        <Route
          key={route.path}
          path={
            route.exact
              ? route.path
              : `${route.path === '/' ? '' : route.path}/*`
          }
          element={
            route.isProtected ? (
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <route.component />
              </ProtectedRoute>
            ) : (
              <GuestRoute isLoggedIn={isLoggedIn}>
                <route.component />
              </GuestRoute>
            )
          }
        />
      ))}
    </RouterRoutes>
  )
})

RouteTree.displayName = 'RouteTree'

export const Routes = () => {
  // Cross-tab auth leadership changes as tabs become hidden/visible. Keep
  // those internal scheduler renders outside the active application tree;
  // routes only need to reconcile when the public login result changes.
  const isLoggedIn = useIsLoggedIn()

  return <RouteTree isLoggedIn={isLoggedIn} />
}
